// src/utils/fileSystem.js
/**
 * Hilfsfunktionen für den Dateisystem-Zugriff mit automatischer Wiederverbindung
 * Nutzt die moderne File System Access API
 */

// Datenbankname und Store für persistente Handle-Speicherung
const DB_NAME = 'FileSystemHandleDB';
const STORE_NAME = 'directoryHandles';
const DB_VERSION = 1;

/**
 * Öffnet die IndexedDB-Datenbank für die Speicherung von Dateisystem-Handles
 * @returns {Promise<IDBDatabase>} Datenbankverbindung
 */
const openHandleDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Erstellt den Object Store falls nötig
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'category' });
      }
    };
    
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
};

/**
 * Speichert einen Verzeichnis-Handle in der IndexedDB für spätere Verwendung
 * @param {string} category - Kategorie des Handles (z.B. 'engeraume')
 * @param {FileSystemDirectoryHandle} handle - Der Verzeichnis-Handle
 * @returns {Promise<boolean>} Erfolg der Speicherung
 */
const storeDirectoryHandle = async (category, handle) => {
  try {
    if (!handle || !('kind' in handle) || handle.kind !== 'directory') {
      throw new Error('Ungültiger Verzeichnis-Handle');
    }
    
    // Kategorie normalisieren
    const normalizedCategory = category.toLowerCase().replace(/\s+/g, '');
    
    // Handle im globalen Objekt speichern für sofortigen Zugriff
    window.directoryHandles = window.directoryHandles || {};
    window.directoryHandles[normalizedCategory] = handle;
    
    // Handle auch in IndexedDB speichern für Persistenz
    const db = await openHandleDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      // Wir speichern auch den Namen und Pfad für informative Zwecke
      const record = {
        category: normalizedCategory,
        name: handle.name,
        timestamp: new Date().toISOString(),
        // Der eigentliche Handle-Objekt wird gespeichert
        handle: handle
      };
      
      const request = store.put(record);
      
      request.onsuccess = () => {
        // Auch im localStorage den Pfadnamen speichern für einfachen Zugriff
        localStorage.setItem(`${normalizedCategory}Speicherort`, handle.name);
        resolve(true);
      };
      
      request.onerror = () => reject(request.error);
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Fehler beim Speichern des Verzeichnis-Handles:', error);
    return false;
  }
};

/**
 * Lädt einen gespeicherten Verzeichnis-Handle aus der IndexedDB
 * @param {string} category - Kategorie des Handles (z.B. 'engeraume')
 * @returns {Promise<FileSystemDirectoryHandle|null>} Der gespeicherte Handle oder null
 */
const retrieveDirectoryHandle = async (category) => {
  try {
    // Kategorie normalisieren
    const normalizedCategory = category.toLowerCase().replace(/\s+/g, '');
    
    // Prüfen, ob wir den Handle bereits im Speicher haben
    if (window.directoryHandles && window.directoryHandles[normalizedCategory]) {
      return window.directoryHandles[normalizedCategory];
    }
    
    // Ansonsten aus der Datenbank laden
    const db = await openHandleDatabase();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(normalizedCategory);
      
      request.onsuccess = () => {
        const record = request.result;
        if (record && record.handle) {
          // Im globalen Objekt cachen für schnelleren Zugriff
          window.directoryHandles = window.directoryHandles || {};
          window.directoryHandles[normalizedCategory] = record.handle;
          resolve(record.handle);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => reject(request.error);
      
      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Fehler beim Laden des Verzeichnis-Handles:', error);
    return null;
  }
};

/**
 * Zeigt einen Dialog zur Auswahl eines Verzeichnisses und speichert den Handle
 * @param {string} category - Kategorie des Verzeichnisses (z.B. 'engeraume')
 * @returns {Promise<string|null>} Name des ausgewählten Verzeichnisses oder null
 */
export const selectDirectory = async (category) => {
  try {
    // Prüfen, ob die File System Access API verfügbar ist
    if (!('showDirectoryPicker' in window)) {
      alert('Ihr Browser unterstützt die Ordnerauswahl nicht. Bitte verwenden Sie Chrome oder Edge.');
      return null;
    }
    
    // Sichere, kurze ID erzeugen (nur a-z0-9_- und max. 32 Zeichen)
    const normalizedCategory = String(category || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9_-]/g, '');
    let pickerId = `fs-${normalizedCategory}`;
    if (pickerId.length > 32) {
      pickerId = pickerId.slice(0, 32);
    }

    // Dialog zur Ordnerauswahl anzeigen
    const dirHandle = await window.showDirectoryPicker({
      id: pickerId,
      mode: 'readwrite', // Wir benötigen Lese- und Schreibzugriff
      startIn: 'documents' // Beginnt im Dokumente-Ordner
    });
    
    // Handle speichern
    await storeDirectoryHandle(category, dirHandle);
    
    // Aktuellen Handle auch im globalen Kontext speichern
    window.currentDirHandle = dirHandle;
    
    return dirHandle.name;
  } catch (error) {
    console.error('Fehler beim Auswählen des Ordners:', error);
    
    if (error.name === 'AbortError') {
      // Benutzer hat den Dialog abgebrochen
      return null;
    }
    
    alert(`Fehler beim Auswählen des Ordners: ${error.message}`);
    return null;
  }
};

/**
 * Versucht, automatisch mit dem letzten Verzeichnis für eine Kategorie zu verbinden
 * @param {string} category - Kategorie des Verzeichnisses (z.B. 'engeraume')
 * @returns {Promise<boolean>} Ob die Verbindung erfolgreich war
 */
export const autoReconnectDirectory = async (category) => {
  try {
    // Prüfen, ob die benötigten APIs verfügbar sind
    if (!('showDirectoryPicker' in window) || !('indexedDB' in window)) {
      console.warn('Browser unterstützt nicht die benötigten APIs für automatische Wiederverbindung');
      return false;
    }
    
    // Kategorie normalisieren
    const normalizedCategory = category.toLowerCase().replace(/\s+/g, '');
    
    // Gespeicherten Handle laden
    const handle = await retrieveDirectoryHandle(normalizedCategory);
    
    if (!handle) {
      console.log(`Kein gespeicherter Handle für Kategorie '${category}' gefunden`);
      return false;
    }
    
    // Prüfen, ob wir noch Zugriff haben oder Berechtigungen neu anfordern müssen
    let permission;
    try {
      // Versuchen, die aktuelle Berechtigung zu prüfen
      permission = await handle.queryPermission({ mode: 'readwrite' });
    } catch (error) {
      console.warn('Fehler beim Abfragen der Berechtigung:', error);
      permission = 'denied';
    }
    
    // Wenn wir keine Berechtigung haben, müssen wir sie anfordern
    if (permission !== 'granted') {
      try {
        // Benutzer benachrichtigen
        const folderName = localStorage.getItem(`${normalizedCategory}Speicherort`) || handle.name;
        
        // Dialog zur Ordnerauswahl anzeigen, mit dem Namen des letzten Ordners
        // Hinweis: Wir verwenden den gleichen showDirectoryPicker wie bei der ersten Auswahl,
        // damit der Benutzer den richtigen Ordner auswählen kann
        const userMsg = `Bitte wählen Sie erneut den Ordner "${folderName}" aus, um den Zugriff zu gewähren.`;
        alert(userMsg);
        
        const newHandle = await window.showDirectoryPicker({
          id: `file-system-access-${category}`,
          mode: 'readwrite',
          startIn: 'documents'
        });
        
        // Prüfen, ob der Benutzer den gleichen Ordner ausgewählt hat
        if (newHandle.name !== handle.name) {
          console.log(`Benutzer hat einen anderen Ordner ausgewählt (${newHandle.name} statt ${handle.name})`);
          // Wir aktualisieren trotzdem den Handle
          await storeDirectoryHandle(category, newHandle);
        } else {
          // Aktuellen Handle aktualisieren
          await storeDirectoryHandle(category, newHandle);
        }
        
        // Aktuellen Handle auch im globalen Kontext speichern
        window.currentDirHandle = newHandle;
        
        return true;
      } catch (error) {
        console.error('Fehler bei der Wiederverbindung:', error);
        return false;
      }
    }
    
    // Wenn wir bereits Zugriff haben, ist alles gut
    console.log(`Automatisch mit Verzeichnis "${handle.name}" verbunden`);
    
    // Aktuellen Handle im globalen Kontext speichern
    window.currentDirHandle = handle;
    
    return true;
  } catch (error) {
    console.error('Fehler bei der automatischen Wiederverbindung:', error);
    return false;
  }
};

/**
 * Prüft, ob bereits ein Verzeichnis für eine Kategorie ausgewählt wurde
 * @param {string} category - Kategorie des Verzeichnisses (z.B. 'engeraume')
 * @returns {Promise<boolean>} Ob ein Verzeichnis ausgewählt wurde
 */
export const hasSelectedDirectory = async (category) => {
  try {
    // Kategorie normalisieren
    const normalizedCategory = category.toLowerCase().replace(/\s+/g, '');
    
    // Prüfen, ob wir den Handle im Speicher haben
    if (window.directoryHandles && window.directoryHandles[normalizedCategory]) {
      return true;
    }
    
    // Versuchen, den Handle aus der Datenbank zu laden
    const handle = await retrieveDirectoryHandle(normalizedCategory);
    return !!handle;
  } catch (error) {
    console.error('Fehler beim Prüfen des ausgewählten Verzeichnisses:', error);
    return false;
  }
};

/**
 * Speichert eine Datei im ausgewählten Verzeichnis
 * @param {string} category - Kategorie des Verzeichnisses (z.B. 'engeraume')
 * @param {string} fileName - Name der zu speichernden Datei
 * @param {Blob|ArrayBuffer|string} content - Inhalt der Datei
 * @returns {Promise<boolean>} Ob das Speichern erfolgreich war
 */
export const saveFileToDirectory = async (category, fileName, content) => {
  try {
    // Kategorie normalisieren
    const normalizedCategory = category.toLowerCase().replace(/\s+/g, '');
    
    // Handle laden oder abrufen
    let dirHandle = window.currentDirHandle;
    
    if (!dirHandle) {
      dirHandle = await retrieveDirectoryHandle(normalizedCategory);
      
      if (!dirHandle) {
        throw new Error(`Kein Verzeichnis für '${category}' ausgewählt`);
      }
      
      // Aktuellen Handle im globalen Kontext speichern
      window.currentDirHandle = dirHandle;
    }
    
    // Berechtigungen prüfen
    let permission;
    try {
      permission = await dirHandle.queryPermission({ mode: 'readwrite' });
    } catch (error) {
      console.warn('Fehler beim Abfragen der Berechtigung:', error);
      permission = 'denied';
    }
    
    // Wenn wir keine Berechtigung haben, müssen wir sie anfordern
    if (permission !== 'granted') {
      permission = await dirHandle.requestPermission({ mode: 'readwrite' });
      
      if (permission !== 'granted') {
        throw new Error('Keine Berechtigung zum Schreiben im ausgewählten Verzeichnis');
      }
    }
    
    // Datei im Verzeichnis erstellen/überschreiben
    const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    
    // Inhalt schreiben
    await writable.write(content);
    await writable.close();
    
    console.log(`Datei "${fileName}" erfolgreich im Verzeichnis "${dirHandle.name}" gespeichert`);
    return true;
  } catch (error) {
    console.error('Fehler beim Speichern der Datei:', error);
    throw error; // Fehler weiterleiten für spezifischere Behandlung
  }
};

/**
 * Lädt alle Dateien eines bestimmten Typs aus dem ausgewählten Verzeichnis
 * @param {string} category - Kategorie des Verzeichnisses (z.B. 'engeraume')
 * @param {string} filePattern - Muster für die Dateinamen (z.B. 'Liste enge Räume FDB')
 * @param {string} fileExtension - Dateiendung (z.B. '.xlsx')
 * @returns {Promise<Array<{name: string, lastModified: number, file: File}>>} Liste der gefundenen Dateien
 */
export const listFilesInDirectory = async (category, filePattern, fileExtension) => {
  try {
    // Kategorie normalisieren
    const normalizedCategory = category.toLowerCase().replace(/\s+/g, '');
    
    // Handle laden oder abrufen
    let dirHandle = window.currentDirHandle;
    
    if (!dirHandle) {
      dirHandle = await retrieveDirectoryHandle(normalizedCategory);
      
      if (!dirHandle) {
        throw new Error(`Kein Verzeichnis für '${category}' ausgewählt`);
      }
      
      // Aktuellen Handle im globalen Kontext speichern
      window.currentDirHandle = dirHandle;
    }
    
    // Berechtigungen prüfen
    let permission;
    try {
      permission = await dirHandle.queryPermission({ mode: 'read' });
    } catch (error) {
      console.warn('Fehler beim Abfragen der Berechtigung:', error);
      permission = 'denied';
    }
    
    // Wenn wir keine Berechtigung haben, müssen wir sie anfordern
    if (permission !== 'granted') {
      permission = await dirHandle.requestPermission({ mode: 'read' });
      
      if (permission !== 'granted') {
        throw new Error('Keine Berechtigung zum Lesen im ausgewählten Verzeichnis');
      }
    }
    
    // Alle passenden Dateien im Verzeichnis sammeln
    const files = [];
    
    for await (const entry of dirHandle.values()) {
      if (entry.kind === 'file' && 
          entry.name.startsWith(filePattern) && 
          entry.name.endsWith(fileExtension)) {
        const file = await entry.getFile();
        files.push({
          name: entry.name,
          lastModified: file.lastModified,
          file: file
        });
      }
    }
    
    // Nach Änderungsdatum sortieren (neueste zuerst)
    files.sort((a, b) => b.lastModified - a.lastModified);
    
    return files;
  } catch (error) {
    console.error('Fehler beim Auflisten der Dateien:', error);
    throw error; // Fehler weiterleiten für spezifischere Behandlung
  }
};

/**
 * Lädt eine Datei aus dem ausgewählten Verzeichnis
 * @param {string} category - Kategorie des Verzeichnisses (z.B. 'engeraume')
 * @param {string} fileName - Name der zu ladenden Datei
 * @returns {Promise<File|null>} Die geladene Datei oder null
 */
export const loadFileFromDirectory = async (category, fileName) => {
  try {
    // Kategorie normalisieren
    const normalizedCategory = category.toLowerCase().replace(/\s+/g, '');
    
    // Handle laden oder abrufen
    let dirHandle = window.currentDirHandle;
    
    if (!dirHandle) {
      dirHandle = await retrieveDirectoryHandle(normalizedCategory);
      
      if (!dirHandle) {
        throw new Error(`Kein Verzeichnis für '${category}' ausgewählt`);
      }
      
      // Aktuellen Handle im globalen Kontext speichern
      window.currentDirHandle = dirHandle;
    }
    
    // Berechtigungen prüfen
    let permission;
    try {
      permission = await dirHandle.queryPermission({ mode: 'read' });
    } catch (error) {
      console.warn('Fehler beim Abfragen der Berechtigung:', error);
      permission = 'denied';
    }
    
    // Wenn wir keine Berechtigung haben, müssen wir sie anfordern
    if (permission !== 'granted') {
      permission = await dirHandle.requestPermission({ mode: 'read' });
      
      if (permission !== 'granted') {
        throw new Error('Keine Berechtigung zum Lesen im ausgewählten Verzeichnis');
      }
    }
    
    // Datei im Verzeichnis suchen
    try {
      const fileHandle = await dirHandle.getFileHandle(fileName);
      const file = await fileHandle.getFile();
      return file;
    } catch (error) {
      if (error.name === 'NotFoundError') {
        console.log(`Datei "${fileName}" wurde nicht gefunden`);
        return null;
      }
      throw error;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Datei:', error);
    throw error; // Fehler weiterleiten für spezifischere Behandlung
  }
};